import type { PreguntaGuia } from 'src/app/utils/liquidacion-guia';

/**
 * PREGUNTAS FRECUENTES DE LA GUÍA DE LIQUIDACIÓN (GUI-04).
 *
 * Se mantienen AQUÍ, en el código, por PR (decisión de Mario 25-oct «Nosotros, en el código»).
 * Las respuestas salen del «Manual de la liquidación por perfil» (25-oct-2026): si algo no está en el manual, no va aquí.
 * Cada una lleva `id` único, los `roles` a los que les sirve (tesorero · gerente · verificador · consulta) y,
 * si habla de lo que ve o recibe el comercio, `comercio: true`.
 */
export const PREGUNTAS_GUIA: PreguntaGuia[] = [
  {
    id: 'guia-001', roles: ['tesorero', 'gerente', 'consulta'],
    pregunta: '¿Cómo empiezo el día en Liquidación?',
    respuesta: 'Entra a Liquidación, pestaña Cierre, y pulsa Abrir el cierre. Si dice «Primero cierra el día X», cierra ese día antes: los días se abren en orden. Luego pulsa Hacer corte para traer todo lo que venció desde el corte anterior.',
  },
  {
    id: 'guia-002', roles: ['tesorero'],
    pregunta: '¿Qué hago si el corte no cuadra?',
    respuesta: 'Mira la tarjeta Cuadre de caja: tiene que decir que cuadra. Si un corte no cuadra, no se guarda y la pantalla avisa la diferencia. Avisa: el corte no se guarda hasta que cuadre.',
  },
  {
    id: 'guia-003', roles: ['tesorero'],
    pregunta: '¿Cómo envío una tanda?',
    respuesta: 'En la tabla de lo que vence por comercio, revisa cada fila y marca con Marcar los comercios que se pagan ahora. Luego pulsa Enviar la tanda N: la consola dice qué banda le toca y quién tiene que firmar. Si la banda es del tesorero, la tanda queda firmada al enviarla.',
  },
  {
    id: 'guia-004', roles: ['tesorero'],
    pregunta: '¿Qué hago con un comercio que no se puede pagar hoy?',
    respuesta: 'Usa Pasar a mañana y elige el motivo (por ejemplo, cuenta no lista o pedido del comercio). Queda como atrasado y el comercio lo ve así en su portal, con el motivo.',
    comercio: true,
  },
  {
    id: 'guia-005', roles: ['tesorero', 'gerente'],
    pregunta: '¿Qué pasa con un comercio cuyo neto es cero o negativo?',
    respuesta: 'No se paga ese día. Por ejemplo, por una devolución: su saldo se descuenta solo del próximo pago. No hay que hacer nada.',
  },
  {
    id: 'guia-006', roles: ['tesorero', 'gerente'],
    pregunta: '¿Cómo cierro el día?',
    respuesta: 'Pulsa Cerrar el día y revisa la vista previa: tandas sin firmar que se devuelven, comercios que pasan a mañana con su motivo y saldos que se arrastran. Si queda dinero sin liquidar, marca la casilla de responsable: tu nombre queda en el acta. Confirma y descarga el Acta en PDF y en Excel desde el Resumen. El día no se reabre: lo que quede mal se corrige con un ajuste en la liquidación siguiente.',
  },
  {
    id: 'guia-007', roles: ['gerente'],
    pregunta: '¿Cómo firmo una tanda?',
    respuesta: 'Entra a Liquidación › Por aprobar: ahí están solo las tandas que te toca firmar. Revisa las alertas, descarga el Excel para auditar y, si cuadra, pulsa Aprobar la tanda N. Si la banda pide dos firmas, la tanda espera a la segunda persona, en orden: gerente y luego director.',
  },
  {
    id: 'guia-008', roles: ['gerente'],
    pregunta: '¿Qué hago si una tanda no cuadra?',
    respuesta: 'Pulsa Devolver con observación, escribe qué hay que revisar en «Observación para el tesorero» y confirma con Devolver.',
  },
  {
    id: 'guia-009', roles: ['gerente', 'tesorero'],
    pregunta: '¿Por qué no me deja firmar una tanda?',
    respuesta: 'Porque tú la preparaste, ya la firmaste, tu rol no alcanza para esa banda, o eres SUPER_ADMIN (no firma pagos). Debajo de tu bandeja, «Esperan otra firma» dice cuál es el motivo. Quien crea no aprueba, y quien prepara una tanda no la firma (salvo la banda del tesorero).',
  },
  {
    id: 'guia-010', roles: ['gerente'],
    pregunta: '¿Cómo creo las condiciones de un comercio?',
    respuesta: 'En Gestión de Comercios, abre el comercio, pestaña Condiciones, y pulsa Crear la primera versión (o la de versión nueva). Son cuatro pasos: tipo de contrato, plazo y comisión, cobros y revisar. Queda «Sin aprobar» hasta que la otra persona (gerente o director) la apruebe: quien la creó no puede aprobarla. Las condiciones nunca se editan: si el acuerdo cambia, se crea una versión nueva que rige desde una fecha.',
  },
  {
    id: 'guia-011', roles: ['verificador', 'gerente', 'tesorero'],
    pregunta: '¿Cómo verifico la cuenta de un comercio?',
    respuesta: 'Abre Gestión de Comercios › el comercio › pestaña Cuentas. Compara la cuenta con la constancia del banco que mandó el comercio y pulsa Verificar en la fila: queda Verificada. La cuenta la carga el tesorero y la verifica otra persona: quien la carga no la verifica.',
  },
  {
    id: 'guia-012', roles: ['verificador', 'tesorero'],
    pregunta: '¿Qué tiene que cumplir el número de cuenta?',
    respuesta: 'Tiene que tener 20 dígitos y empezar con el código de su banco. Si no, el comercio queda fuera del lote con ese motivo y hay que corregirlo en la ficha del comercio.',
  },
  {
    id: 'guia-013', roles: ['verificador', 'gerente', 'tesorero'],
    pregunta: '¿Cómo cambia un comercio su cuenta de cobro?',
    respuesta: 'Se carga una cuenta nueva y se verifica igual; la vieja se da de baja con Dar de baja la cuenta. El historial queda en Ver historial.',
  },
  {
    id: 'guia-014', roles: ['tesorero'],
    pregunta: '¿Cómo armo y pago un lote?',
    respuesta: 'En Lotes, en «Tandas aprobadas sin lote», elige Cómo se paga y la Cuenta origen y pulsa Armar lote. Luego Generar archivo (se fija la tasa BCV del día), Descargar y pagar en la banca con ese archivo, y al terminar pulsa Ya pagué en la banca. Cuando el banco responda, descarga el Excel para marcar la respuesta, llénalo y súbelo con Subir respuesta.',
  },
  {
    id: 'guia-015', roles: ['tesorero', 'gerente'],
    pregunta: '¿Qué hago con un pago que el banco no procesó?',
    respuesta: 'Los no procesados van a Por resolver. Corrige la causa con el comercio (por ejemplo, una cuenta nueva verificada), marca los pagos y pulsa Propuesta nueva: van a un lote nuevo y la tanda no se vuelve a firmar. Si todavía no se puede pagar, usa Retener con motivo.',
  },
  {
    id: 'guia-016', roles: ['tesorero'],
    pregunta: '¿Por qué no sube la respuesta del banco?',
    respuesta: 'Alguna fila está mal llena: «Procesado» sin referencia o «No procesado» sin motivo. Una fila mal llena rechaza todo el archivo y la pantalla lista las filas a corregir; corrige y súbelo de nuevo.',
  },
  {
    id: 'guia-017', roles: ['consulta', 'tesorero', 'gerente'],
    pregunta: 'Un comercio llama y dice que no le llegó el pago. ¿Qué hago?',
    respuesta: 'Busca la liquidación en Liquidación › Consultas › Buscar con el número de pagaré, la factura de su caja, el LIQ-… o la referencia bancaria. Si dice pagado, dale la referencia y el comprobante; si dice no procesado, está en Por resolver.',
    comercio: true,
  },
  {
    id: 'guia-018', roles: ['consulta', 'tesorero', 'gerente'],
    pregunta: '¿Qué ve el comercio en su portal sobre su liquidación?',
    respuesta: 'Solo el administrador del comercio la ve. En Resumen, la tarjeta «Tu liquidación» le dice en qué paso va lo de hoy, cuánto fue el último pago y cuándo es el próximo. En Liquidaciones ve cada pago con su estado (abiertas, atrasadas, cobradas) y su calendario día por día. Los montos aparecen en bolívares grande y en dólares pequeño.',
    comercio: true,
  },
  {
    id: 'guia-019', roles: ['consulta', 'tesorero', 'gerente'],
    pregunta: '¿Qué correo y qué comprobante recibe el comercio cuando se le paga?',
    respuesta: 'Le llega un correo con asunto «Pago de su liquidación LIQ-… · monto», con el monto en Bs y USD, la tasa, la referencia y la cuenta enmascarada, más el comprobante en PDF adjunto. Va al contacto de finanzas; si no hay, al administrador. El comprobante muestra cada descuento con su porcentaje y su base, el cargo por uso, ajustes, arrastre y el neto. El detalle venta por venta está en el portal.',
    comercio: true,
  },
  {
    id: 'guia-020', roles: ['consulta', 'gerente'],
    pregunta: '¿Qué significa «Por liquidar», «En proceso de liquidación» y «Atrasado» en una venta?',
    respuesta: '«Por liquidar · se paga el dd/mm»: la venta está hecha y tiene fecha de pago según su contrato. «En proceso de liquidación»: CrediTotal ya la aprobó y está en camino al banco. «Liquidado · pagado el dd/mm»: el banco confirmó el pago, con referencia y comprobante. «Atrasado»: pasó su fecha sin pagarse; se ve el motivo y a quién llamar.',
    comercio: true,
  },
  {
    id: 'guia-021', roles: ['consulta', 'tesorero'],
    pregunta: '¿Cómo reenvío el aviso de pago a un comercio?',
    respuesta: 'Abre la liquidación y pulsa Reenviar aviso (lo hacen el tesorero o la gerente), con motivo.',
    comercio: true,
  },
  {
    id: 'guia-022', roles: ['gerente', 'consulta'],
    pregunta: '¿Nadie puede firmar una tanda?',
    respuesta: 'Nadie tiene el rol que pide la banda. Hay que asignarlo en Roles › Usuarios y Asignaciones; una cinta ámbar lo avisa con un enlace a Roles.',
  },
];
